/**
 * Command Matrix Component
 * Time-boxed task view: NOW, TODAY, TOMORROW
 */

import React, { useState } from 'react'
import { Button } from '../ui/button'
import { ScrollArea } from '../ui/scroll-area'
import { cn } from '../../lib/utils'
import { 
  Clock, AlertCircle, ChevronRight, MoreVertical,
  Play, Pause, CheckCircle, XCircle, Timer,
  GripVertical, Plus, ArrowRight
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Task {
  id: string
  title: string
  status: string
  priority: string
  dueDate: string | null
  estimatedMinutes: number
  project?: string
  column?: string
  isBlocked: boolean
  blockedReason?: string
  riskScore: number
}

interface CommandMatrixProps {
  matrix: {
    now: Task[]
    today: Task[]
    tomorrow: Task[]
  }
  onTaskClick: (taskId: string) => void
  onTaskDrop?: (taskId: string, from: string, to: string) => void
  minimal?: boolean
  simplified?: boolean
}

function TaskCard({ 
  task, 
  timeframe,
  onClick 
}: { 
  task: Task
  timeframe: 'now' | 'today' | 'tomorrow'
  onClick: () => void 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getPriorityDot = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500'
      case 'high': return 'bg-orange-500'
      case 'medium': return 'bg-blue-500'
      case 'low': return 'bg-gray-400'
      default: return 'bg-gray-400'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return <Play className="w-3 h-3 text-blue-600" />
      case 'TODO': return <Clock className="w-3 h-3 text-gray-400" />
      case 'BLOCKED': return <XCircle className="w-3 h-3 text-red-500" />
      default: return null
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative bg-white border border-gray-200 rounded-xl p-4 cursor-pointer transition-all duration-200",
        "hover:shadow-md hover:border-gray-300 hover:-translate-y-0.5",
        task.isBlocked && "border-red-200 bg-red-50/50",
        task.status === 'IN_PROGRESS' && "border-blue-200 bg-blue-50/50",
        isDragging && "shadow-xl"
      )}
      onClick={onClick}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-3 h-3 text-gray-300" />
      </div>

      <div className="pl-2">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            {getStatusIcon(task.status)}
            <h4 className="text-sm font-medium text-gray-900 line-clamp-2 flex-1 leading-5">
              {task.title}
            </h4>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-3 text-sm">
          {/* Priority */}
          <div className="flex items-center gap-2">
            <span className={cn("w-2 h-2 rounded-full", getPriorityDot(task.priority))} />
            <span className="text-gray-500 capitalize">{task.priority}</span>
          </div>

          {/* Time estimate */}
          {task.estimatedMinutes && (
            <div className="flex items-center gap-1 text-gray-500">
              <Timer className="w-3.5 h-3.5" />
              <span>{task.estimatedMinutes}m</span>
            </div>
          )}

          {/* Blocked indicator */}
          {task.isBlocked && (
            <span className="text-red-600 text-sm font-medium">Blocked</span>
          )}
        </div>
      </div>
    </div>
  )
}

function TimeBox({
  title,
  tasks,
  timeframe,
  onTaskClick,
  description,
  emptyMessage,
  accentColor
}: {
  title: string
  tasks: Task[]
  timeframe: 'now' | 'today' | 'tomorrow'
  onTaskClick: (taskId: string) => void
  description?: string
  emptyMessage?: string
  accentColor: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
      <div className={cn("px-5 py-4", accentColor)}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-white">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-white/80 mt-1">{description}</p>
            )}
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
            {tasks.length}
          </span>
        </div>
      </div>
      
      <div className="flex-1 p-4 overflow-hidden">
        <ScrollArea className="h-full">
          {tasks.length > 0 ? (
            <SortableContext
              items={tasks.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    timeframe={timeframe}
                    onClick={() => onTaskClick(task.id)}
                  />
                ))}
              </div>
            </SortableContext>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <CheckCircle className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">
                {emptyMessage || 'No tasks'}
              </p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  )
}

export default function CommandMatrix({
  matrix,
  onTaskClick,
  onTaskDrop,
  minimal = false,
  simplified = false
}: CommandMatrixProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && onTaskDrop) {
      console.log('Drag end:', active.id, over?.id)
    }
  }

  if (minimal) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-4">
          {matrix.now.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                NOW ({matrix.now.length})
              </h3>
              <div className="space-y-2">
                {matrix.now.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    timeframe="now"
                    onClick={() => onTaskClick(task.id)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {matrix.today.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                TODAY ({matrix.today.length})
              </h3>
              <div className="space-y-2">
                {matrix.today.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    timeframe="today"
                    onClick={() => onTaskClick(task.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </DndContext>
    )
  }

  if (simplified) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              My Tasks
            </h2>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* NOW Column */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  Now
                  <span className="text-sm font-normal text-gray-500">({matrix.now.length})</span>
                </h3>
                <p className="text-sm text-gray-600 mt-1">Next 2 hours</p>
              </div>
              
              <div className="p-6">
                <ScrollArea className="h-80">
                  {matrix.now.length > 0 ? (
                    <SortableContext
                      items={matrix.now.map(t => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {matrix.now.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            timeframe="now"
                            onClick={() => onTaskClick(task.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-center">
                      <CheckCircle className="w-8 h-8 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">Ready for action</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>

            {/* TODAY Column */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  Today
                  <span className="text-sm font-normal text-gray-500">({matrix.today.length})</span>
                </h3>
                <p className="text-sm text-gray-600 mt-1">Rest of today</p>
              </div>
              
              <div className="p-6">
                <ScrollArea className="h-80">
                  {matrix.today.length > 0 ? (
                    <SortableContext
                      items={matrix.today.map(t => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {matrix.today.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            timeframe="today"
                            onClick={() => onTaskClick(task.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-center">
                      <CheckCircle className="w-8 h-8 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">Day is clear</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </DndContext>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-medium text-gray-900">
            My Battle Rhythm
          </h3>
          <Button variant="ghost" size="sm" className="text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100">
            View All Tasks
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-96">
          <TimeBox
            title="NOW"
            description="Next 2 hours"
            tasks={matrix.now}
            timeframe="now"
            onTaskClick={onTaskClick}
            emptyMessage="Ready for action"
            accentColor="bg-gradient-to-r from-red-500 to-red-600"
          />
          
          <TimeBox
            title="TODAY"
            description="2-8 hours"
            tasks={matrix.today}
            timeframe="today"
            onTaskClick={onTaskClick}
            emptyMessage="Day is clear"
            accentColor="bg-gradient-to-r from-blue-500 to-blue-600"
          />
          
          <TimeBox
            title="TOMORROW"
            description="Next day"
            tasks={matrix.tomorrow}
            timeframe="tomorrow"
            onTaskClick={onTaskClick}
            emptyMessage="Tomorrow is open"
            accentColor="bg-gradient-to-r from-pink-500 to-pink-600"
          />
        </div>
      </div>
    </DndContext>
  )
}